import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigProvider } from '@app/app.config.provider';
import { ICalibrationStatusUpdateRequest } from '@models/calibrations/calibration-status-update-request';
import { FORM_IDs as FormIds, IDecayCalibration, IDecayCalibrationSaveRequestDTO } from '@models/decay/decay-calibration';
import { IRecalculationRequest } from '@models/recalculation/recalculation-request';
import { RecalculationTrigger } from '@models/recalculation/recalculation-trigger';
import { LocalStorageKeys } from '@models/store/local-storage-keys';
import { ChangelogService } from '@services/changelog/changelog.service';
import { LocalStorageService } from '@services/local-storage.service';
import { MenuService } from '@services/menu.service';
import { RecalculationService } from '@services/recalculation/recalculation.service';
import { setEstimateColours } from '@shared/decay-helpers';
import { forkJoin, Observable, of } from 'rxjs';
import { map, retry, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class DecayCalibrationService {
  private calibrationEndpoint: string;
  private decayCalibrationsForProductsApiURL: string;
  private rejectEndpoint: string;
  private approveEndpoint: string;

  constructor(
    private readonly appConfig: AppConfigProvider,
    private readonly recalculationService: RecalculationService,
    private readonly changelogService: ChangelogService,
    private readonly http: HttpClient,
    private readonly localStorageService: LocalStorageService,
    private readonly menuService: MenuService
  ) {
    this.appConfig.environment.subscribe(env => {
      this.calibrationEndpoint = `${env.API.DECAY}/api/decay/calibration`;
      this.approveEndpoint = `${this.calibrationEndpoint}/approve`;
      this.rejectEndpoint = `${this.calibrationEndpoint}/reject`;
      this.decayCalibrationsForProductsApiURL = `${env.API.DECAY}/api/decay/calibration/forproduct`;
    });
  }

  /**
   * This will save the entire calibration for the decay.
   * It will also clear the calibration stored in the local storage for this product
   */
  public proposeCalibration(requestParams: IDecayCalibrationSaveRequestDTO): Observable<IDecayCalibration> {
    return this.http.post<IDecayCalibration>(this.calibrationEndpoint, requestParams).pipe(
      // Assigning these new results ID's
      map((proposedCalibration: IDecayCalibration) => {
        setEstimateColours(proposedCalibration.DecayEstimateSetting);
        proposedCalibration.DecayEstimateSetting.forEach(est => {
          est.ExcludeAcquisitionIds = proposedCalibration.ExcludeAcquisitionIds;
          est.StartDate = proposedCalibration.StartDate;
          est.IsValid = true;
        });
        return proposedCalibration;
      }),
      tap(cal => {
        const prodId = (cal && cal.ProductId) || this.menuService.selectedProduct.ProductId;
        this.localStorageService.clearFromLocalStorage(LocalStorageKeys.Decays, prodId);

        // RECALC CALL SAVE
        if (prodId) {
          let trigger: RecalculationTrigger = null;
          const productIds: number[] = [];
          trigger = RecalculationTrigger.CalibrationSave;
          if (trigger !== null) {
            productIds.push(prodId);

            const request: IRecalculationRequest = {
              ProductIds: productIds,
              RecalculationTrigger: trigger
            };

            this.recalculationService
              .startCalculation(request)
              .toPromise()
              .catch(err => {
                console.warn(`Error recalculating calibrations`, { request, error: err });
              });
          }
        } else {
          console.warn('NO ProductId at proposeCalibration()', { prodId, ProductId: this.menuService.selectedProduct, requestParams });
        }
      })
    );
  }

  public saveAndApproveCalibration(requestParams: IDecayCalibrationSaveRequestDTO) {
    return this.http.post<IDecayCalibration>(`${this.calibrationEndpoint}/save-approve`, requestParams).pipe(
      // Assigning these new results ID's
      map((proposedAndApprovedCalibration: IDecayCalibration) => {
        setEstimateColours(proposedAndApprovedCalibration.DecayEstimateSetting);
        return proposedAndApprovedCalibration;
      }),
      tap(cal => {
        const prodId = (cal && cal.ProductId) || this.menuService.selectedProduct.ProductId;
        if (prodId) {
          this.localStorageService.clearFromLocalStorage(LocalStorageKeys.Decays, prodId);
          const recalcRequest: IRecalculationRequest = {
            ProductIds: [prodId],
            RecalculationTrigger: RecalculationTrigger.CalibrationSaveApprove
          };
          this.recalculationService.startCalculation(recalcRequest).toPromise();
        }
      })
    );
  }

  public approveCalibration(calibration: IDecayCalibration): Promise<IDecayCalibration> {
    const requestObj: ICalibrationStatusUpdateRequest = {
      CalibrationId: calibration.DecayCalibrationId,
      Comment: calibration.Comment,
      FormId: FormIds.Approve,
      DateTimeCreated: calibration.DateTimeCreated,
      DateTimeModified: calibration.DateTimeModified
    };

    return this.http
      .post<IDecayCalibration>(this.approveEndpoint, requestObj)
      .pipe(
        map(approvedCalibration => {
          setEstimateColours(approvedCalibration.DecayEstimateSetting);
          return approvedCalibration;
        }),
        tap(() => {
          this.localStorageService.clearFromLocalStorage(LocalStorageKeys.Decays, calibration.ProductId);

          if (calibration.ProductId) {
            const recalcRequest: IRecalculationRequest = {
              ProductIds: [calibration.ProductId],
              RecalculationTrigger: RecalculationTrigger.CalibrationApprove
            };

            this.recalculationService.startCalculation(recalcRequest).toPromise();
          }
        })
      )
      .toPromise();
  }

  public rejectCalibration(calibration: IDecayCalibration): Promise<IDecayCalibration> {
    const requestObj: ICalibrationStatusUpdateRequest = {
      CalibrationId: calibration.DecayCalibrationId,
      Comment: calibration.Comment,
      DateTimeCreated: calibration.DateTimeCreated,
      DateTimeModified: calibration.DateTimeModified,
      FormId: FormIds.Reject
    };
    return this.http
      .post<IDecayCalibration>(this.rejectEndpoint, requestObj)
      .pipe(
        map(rejectedCalibration => {
          setEstimateColours(rejectedCalibration.DecayEstimateSetting);
          return rejectedCalibration;
        }),
        tap(() => {
          this.localStorageService.clearFromLocalStorage(LocalStorageKeys.Decays, calibration.ProductId);

          if (calibration.ProductId) {
            const recalcRequest: IRecalculationRequest = {
              ProductIds: [calibration.ProductId],
              RecalculationTrigger: RecalculationTrigger.CalibrationApprove
            };

            this.recalculationService.startCalculation(recalcRequest, 'Calibration Rejected').toPromise();
          }
        })
      )
      .toPromise();
  }

  public getCalibrations({
    productId = null,
    excludeInactive = false,
    includeChangelogs = true
  }: {
    productId?: number;
    excludeInactive?: boolean;
    includeChangelogs?: boolean;
  }): Observable<IDecayCalibration[]> {
    const url = productId === null ? `${this.calibrationEndpoint}` : `${this.decayCalibrationsForProductsApiURL}/${productId}`;

    let params: HttpParams = new HttpParams();
    if (excludeInactive) {
      params = params.append('excludeInactive', 'true');
    }

    const calibrations$ = this.http
      .get<IDecayCalibration[]>(url, { params })
      .pipe(
        map(calibrations => {
          calibrations.forEach(calibration => {
            if (calibration.DecayEstimateSetting) {
              calibration.DecayEstimateSetting.forEach(est => {
                est.StartDate = calibration.StartDate;
                est.ExcludeAcquisitionIds = calibration.ExcludeAcquisitionIds;
                est.DecayCalibrationType = calibration.DecayCalibrationType;
              });
            }
          });
          return calibrations.sort((a, b) => new Date(b.DateTimeCreated).getTime() - new Date(a.DateTimeCreated).getTime());
        }),
        retry(2)
      );

    const changelog$ =
      !includeChangelogs || productId === null ? of([]) : this.changelogService.getChangelogForDecayCalibrationForProduct(productId);

    const result$ = forkJoin([calibrations$, changelog$]).pipe(
      map(([calibrations, changelogs]) => {
        calibrations.forEach(calibration => {
          calibration.ChangeLogs = this.getParsedCalibrationChangelogs(calibration, changelogs);
        });
        return calibrations;
      })
    );

    return result$;
  }

  private getParsedCalibrationChangelogs(calibration: IDecayCalibration, changelogs: any[]): any[] {
    changelogs = changelogs
      // Associate changelogs with his calibration
      .filter(changelog => changelog.CalibrationId === calibration.DecayCalibrationId);

    return changelogs;
  }

  public getLatestCalibrationForProduct(productId: number): Observable<IDecayCalibration> {
    return this.getCalibrations({ productId, excludeInactive: true, includeChangelogs: false }).pipe(
      map(res => {
        const calibration = res[0];
        if (calibration) {
          const dateTimeCreated = new Date(calibration.DateTimeCreated);
          const dateTimeModified = new Date(calibration.DateTimeModified);

          const calibrationDate = dateTimeCreated > dateTimeModified ? dateTimeCreated : dateTimeModified;

          if (calibration.DecayEstimateSetting) {
            calibration.DecayEstimateSetting.forEach(est => {
              est.DateTimeCreated = calibrationDate.toJSON();
            });
          }

          return calibration;
        } else {
          return null;
        }
      })
    );
  }
}
